import { Bell, Mail, Phone, Train, User } from "lucide-react";
import { Link, useLocation } from "wouter";

import { PresentationLink } from "./PresentationLink";
import { UserGuideLink } from "./UserGuideLink";

import { useGetUnreadNotificationCount } from "@/common/api";
import { Badge, Button, LoadingModal } from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth, useUser } from "@/common/hooks";
export function Head() {
  const {
    auth: { isAuthenticated, id: userId },
  } = useAuth();

  const { permissions, query } = useUser();

  const [location] = useLocation();

  const { data: unreadCountData } = useGetUnreadNotificationCount(
    { userId: userId ?? "" },
    {
      query: {
        enabled: isAuthenticated && !!userId,
        refetchInterval: 60_000,
      },
    },
  );

  // Determine if we're on the landing page
  const isLandingPage = location === ROUTES.ROOT && !isAuthenticated;

  const unreadCount = unreadCountData?.data ?? 0;

  const contactInfo = (
    <div
      className={`hidden lg:flex items-center gap-10 text-sm ${isLandingPage ? "text-white" : "text-black"}`}
    >
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Phone className="h-3 w-3" />
        <a href="tel:+7 (800) 700-19-94">8 (800) 700 19 94</a>
      </div>
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Mail className="h-3 w-3" />
        <a href="mailto:eis@it4prof.ru" target="_blank">
          eis@it4prof.ru
        </a>
      </div>
    </div>
  );

  if (query.isLoading) {
    return <LoadingModal isOpen={true} />;
  }

  return (
    // Desktop - версия, менее 768рх скрывается
    <header
      className={`hidden md:block absolute top-0 right-0 z-50 bg-transparent ${isAuthenticated ? "left-64" : "left-0"}`}
    >
      <div className="max-w-7xl lg:mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex justify-between items-center py-4 ${!isAuthenticated ? "gap-6" : ""}`}
        >
          {isAuthenticated ? (
            <>
              <div className="flex-1"></div>
              <div className="flex items-center">{contactInfo}</div>
              <div className="flex gap-3">
                <Link href={ROUTES.NOTIFICATIONS}>
                  <Button
                    variant="ghost"
                    className={`relative p-2 h-10 w-10 ml-8 ${isLandingPage ? "text-white hover:bg-white/10" : "text-black hover:bg-black/10"}`}
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 text-xs p-0 flex items-center justify-center bg-red-500"
                      >
                        {unreadCount}
                      </Badge>
                    )}
                  </Button>
                </Link>

                {permissions?.profile?.vew_profile && (
                  <Link href={ROUTES.PROFILE}>
                    <Button
                      variant="ghost"
                      className={`flex items-center gap-2 ${isLandingPage ? "text-white hover:bg-white/10" : "text-black hover:bg-black/10"}`}
                    >
                      <User className="h-4 w-4" />
                      Профиль
                    </Button>
                  </Link>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex shrink-0 items-center transition-opacity">
                <div className="h-20 mr-4 flex items-center justify-center">
                  <img src="/logo-head.png" alt="ЭИС" className="h-10 object-contain" />
                </div>

                <div
                  className={"hidden lg:block text-base font-bold leading-4"}
                  style={{ color: "var(--color-dasha-style)" }}
                >
                  <div>Электронная</div> <div>Информационная</div> <div>Система</div>
                </div>
              </div>

              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-6 gap-y-3">
                {contactInfo}
                <div className="flex items-center gap-3">
                  <PresentationLink classNameBtn="text-sm" />
                  <UserGuideLink classNameBtn="text-sm" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
